import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MailOpen, MessagesSquare, LayoutGrid, Users, PiggyBank, ListChecks, Star, ClipboardList } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
export function FeaturesSection() {
  const integrations = [{
    name: 'Gmail',
    logo: 'https://cdn.magicpatterns.com/uploads/i3m9Dz8tVyFX3XYH5aSycT/gmail-icon.svg'
  }, {
    name: 'Outlook',
    logo: 'https://cdn.magicpatterns.com/uploads/wciZK1t5un9NpvyYG4xtuo/outlook-icon.svg'
  }, {
    name: 'iMessage',
    logo: 'https://cdn.magicpatterns.com/uploads/b7Bk8etBDenG3R7oDnec67/imessage-icon.svg'
  }, {
    name: 'WhatsApp',
    logo: 'https://cdn.magicpatterns.com/uploads/j1eFkudG3snN4BnwPPvfbX/whatsapp-icon.svg'
  }, {
    name: 'Google Drive',
    logo: 'https://cdn.magicpatterns.com/uploads/2yqcSmzQqZi9cdSsmgsLpN/google-drive-icon.svg'
  }, {
    name: 'Notion',
    logo: 'https://cdn.magicpatterns.com/uploads/1kGheTjtnQKFkEbYZYttia/notion-icon.svg'
  }, {
    name: 'Airtable',
    logo: 'https://cdn.magicpatterns.com/uploads/bQ3BrNNoSGHPhJ2LPvGch1/airtable-icon.svg'
  }, {
    name: 'Canva',
    logo: 'https://cdn.magicpatterns.com/uploads/iKQEnA2bTqFX8YLk3u7yRT/canva-logo.png'
  }, {
    name: 'OneDrive',
    logo: 'https://cdn.magicpatterns.com/uploads/uQgGV18a3e7tTLcoaM3Puu/onedrive-icon.svg'
  }, {
    name: 'Tripleseat',
    logo: 'https://cdn.magicpatterns.com/uploads/pBvjpazwySPVaj2JeBgC2A/tripleseat-logo.png'
  }];
  const actions = [{
    label: 'Every event, priced in pipeline',
    icon: LayoutGrid,
    description: 'We scan thousands of events and surface only the ones worth your time, each with an ROI band and a confidence rating.'
  }, {
    label: 'Lock in the right people',
    icon: Users,
    description: 'Every target is scored against your ICP and traced back to a source, so you know exactly who is in the room.'
  }, {
    label: 'Worth the ticket?',
    icon: PiggyBank,
    description: 'Expected meetings, close rate, and ASP against total cost. Approve the spend only when the math works.'
  }, {
    label: 'Your invites, in motion',
    icon: MailOpen,
    description: 'Multi-channel invites across LinkedIn, email, and phone, split between our promoter accounts and your reps.'
  }, {
    label: 'No-show protection',
    icon: MessagesSquare,
    description: 'Every guest carries an attendance forecast, with recovery actions on anyone likely to drop.'
  }, {
    label: 'Attributable meetings',
    icon: Star,
    description: 'A meeting booked within 30 days of the event, before or after, linked to the deal in your CRM.'
  }, {
    label: 'One bill, booked meetings',
    icon: ListChecks,
    description: 'Stop reconciling tickets, flights, and venues. One managed line item, one invoice.'
  }, {
    label: 'First event in two weeks',
    icon: ClipboardList,
    description: 'A short intake gets you an events calendar and your first event on the books within two weeks.'
  }];
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const pillVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  return <section id="features" className="py-20 bg-white" data-id="element-128" data-mp="15tvtame2kjla">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-129" data-mp="15sax6sc3wslb">
        {/* Integrations Carousel */}
        <AnimatedSection data-id="element-130" data-mp="3ljytqmn7kj3l">
          <div className="text-center mb-16" data-id="element-131" data-mp="3qkd1e3kwdr13">
            <p className="text-sm font-semibold text-indigo-600 tracking-wide uppercase mb-8" data-id="element-132" data-mp="3ql5x89ibiw4g">
              A managed events channel for B2B sales teams
            </p>
            <div className="relative overflow-hidden" data-id="element-133" data-mp="5uv6t2suer3n">
              <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" data-id="element-134" data-mp="5tbq5b39ye1c" />
              <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" data-id="element-135" data-mp="3s34wl06o4wu2" />
              <div className="flex animate-marquee hover:[animation-play-state:paused]" data-id="element-136" data-mp="tjo535wxtb1f">
                {[...integrations, ...integrations].map((item, index) => <div key={index} className="flex items-center space-x-3 px-8 flex-shrink-0" data-id="element-137" data-mp="jma145s7aeq0" data-mpi={index}>
                    <img src={item.logo} alt={item.name} className="h-8 w-8 object-contain opacity-70" data-id="element-138" data-mp="ept56perew2d" />
                    <span className="font-medium text-gray-500 whitespace-nowrap" data-id="element-139" data-mp="2zcmkj9nyvl4i">
                      {item.name}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Features Heading */}
        <AnimatedSection delay={0.2} data-id="element-140" data-mp="1osrsvl5ev6cu">
          <div className="text-center mb-12" data-id="element-141" data-mp="1tkja352ofzqb">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-142" data-mp="1tlkva2deq2lr">
              Events That Behave Like a Channel, Not a Line Item
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto" data-id="element-143" data-mp="22ky9178kddca">
              Budget it, forecast it, and compare it against outbound. Turnout
              runs the whole motion and reports back in numbers you already
              trust.
            </p>
          </div>
        </AnimatedSection>

        {/* Action Pills Grid — staggered entrance */}
        <motion.div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} data-id="element-144" data-mp="d8nrpeunl7lt">
          {actions.map((action, index) => {
          const Icon = action.icon;
          return <motion.div key={index} variants={pillVariants} whileHover={{
            scale: 1.02,
            y: -2
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} className="group flex items-center gap-5 px-6 py-5 bg-white border border-indigo-100 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-colors duration-200 cursor-default" data-id="element-145" data-mp="k11ekiqavb2e" data-mpi={index}>
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-indigo-50 group-hover:bg-indigo-100 flex items-center justify-center transition-colors duration-200" data-id="element-146" data-mp="1csl4smlbdpdw">
                  <Icon className="h-6 w-6 text-indigo-600" data-id="element-147" data-mp="1re8pq9zccxxt" />
                </div>
                <div className="min-w-0" data-id="element-148" data-mp="1vftwddla8fzk">
                  <span className="text-lg font-semibold text-gray-900 block leading-tight" data-id="element-149" data-mp="1vesiz2gsnheo">
                    {action.label}
                  </span>
                  <span className="text-sm text-gray-500 leading-snug block mt-0.5" data-id="element-150" data-mp="1mf5icivw2fmo">
                    {action.description}
                  </span>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}